/**
 * Client-side permission check — the UI mirror of the server's `PermissionsGuard.isSatisfied`
 * (`apps/api/src/common/auth/permissions.guard.ts`). Used ONLY to hide affordances the user can't
 * act on; the server still enforces every check (this is not a security boundary).
 *
 * Scope rule (kept consistent with the server): a `resource:action:any` grant is a SUPERSET of
 * `resource:action:own`, so holding `:any` satisfies a check for `:own`. The reverse never holds.
 */

/** True when `granted` satisfies the single `required` permission, honoring the `:any > :own` rule. */
export function permissionSatisfied(required: string, granted: ReadonlySet<string>): boolean {
  if (granted.has(required)) {
    return true;
  }
  if (required.endsWith(':own')) {
    return granted.has(`${required.slice(0, -':own'.length)}:any`);
  }
  return false;
}

/**
 * Build a `can(permission)` checker (and an `all`/`any` variant) bound to a permission set. Pass the
 * authenticated user's `permissions` from `me`. ALL listed permissions must hold for `can` (matches
 * the server's `required.every(...)`).
 */
export function createCan(permissions: readonly string[]): {
  can: (...required: string[]) => boolean;
  canAny: (...required: string[]) => boolean;
} {
  const granted = new Set(permissions);
  return {
    can: (...required) => required.every((p) => permissionSatisfied(p, granted)),
    canAny: (...required) => required.some((p) => permissionSatisfied(p, granted)),
  };
}
